import { mixins, Options } from "vue-class-component"
import { Emit, Prop, Watch } from "vue-property-decorator"
import { gsap } from "gsap"
import cloneDeep from "lodash/cloneDeep"
import isEqual from "lodash/isEqual"
import { Action, Getter } from "s-vuex-class"

import BacklogCommonMetrics from "@/components/Backlog/BacklogCommonMetrics"

import { moveTo } from "@/mixins/moveTo"
import {
  AlertContentItem,
  AlertType,
  BacklogSetup,
  ChangeItemOwner,
  ChangeItemStatus,
  Comment,
  DefinitionOfDone,
  Epic,
  Item,
  ItemAnalytics,
  ItemQueryPriority,
  ItemQueryPriorityType,
  ItemType,
  MyWork,
  MyWorkSectionId,
  Project,
  RemoveItemFromBacklog,
  ResourceEvent,
  ServerResponse,
  ServerSendEventNotification,
  ServerSendSprintUpdateNotification,
  Sprint,
  StructuredDescription,
  Task,
  TaskDraftEdit,
  Team,
  TipsKeyType,
  TipsStepType,
  UpdateObjectValue,
  UpdateStatusParams,
  UpdateTaskList
} from "@/models"
import { $t } from "@/plugins/i18n"
import { emitterDeleteByKey, emitterOn } from "@/plugins/mitt-emitter"
import { Actions, Getters, store } from "@/store"
import { AlertActions, alertModule } from "@/store/modules/alert.module"
import { BacklogActions, backlogModule } from "@/store/modules/backlog.module"
import {
  forwardScaleAnimation,
  positionTipsByElement,
  removeTransform,
  reverseScaleAnimation,
  slideAnimation,
  slideItemAnimationLeft,
  slideItemAnimationRight,
  storyOpeningAnimation,
  storyScalingAnimation
} from "@/utils"
import RequestQueue from "@/utils/requestQueue"

interface ItemSessionData {
  item: Item
  tasks: Task[]
  unsavedChanges?: boolean
  itemFieldsChanged?: (keyof Item)[]
  taskDraftEdits?: TaskDraftEdit[]
}

const ITEM_SESSION_KEY = "item-before-reload"

@Options({
  name: "StoryCommon",
  emits: ["unsavedChanges", "onForceUpdateEditItem", "openedItemBacklogSetupChange", "hideItem"]
})
export class StoryCommon extends mixins(moveTo, BacklogCommonMetrics) {
  @Prop({ default: true }) readonly continueUnsave: boolean
  @Prop() readonly epic: Epic
  @Prop({ default: null }) readonly customIndexForNewItem: number | null
  @Prop({ default: () => [] }) readonly deferredDeletedEvents: RemoveItemFromBacklog[]

  @Getter(Getters.GET_ITEM_CLONE) itemClone: Item
  @Getter(Getters.GET_TASK_DRAFT_EDITS) taskDraftEdits: TaskDraftEdit[]
  @Getter(Getters.GET_BACKLOG_CREATED_ITEM) readonly backlogCreatedItem: Item | null
  @Getter(Getters.TASK_LIST_WAS_EDITED) taskIsEdited: boolean
  @Getter(Getters.THEME_IS_DARK) readonly isDark: boolean
  @Getter(Getters.GET_SESSION_TEAM_ID) readonly teamId: string
  @Getter(Getters.GET_SESSION_TEAM) currentTeam: Team
  @Getter(Getters.GET_DEFINITIONS_LIST) enabledDefinitions: DefinitionOfDone[]
  @Getter(Getters.GET_ITEM_ANALYTICS) itemAnalytics: ItemAnalytics | null
  @Getter(Getters.GET_SELECTED_TIPS_STEP) readonly selectedStep: TipsStepType
  @Getter(Getters.SUBSCRIPTION_MESSAGE_NOTIFICATION) readonly serverSendUpdated: (
    status: ResourceEvent
  ) => ServerSendEventNotification
  @Getter(Getters.ALL_DEFINITIONS_LIST) readonly allDefinitionsList: DefinitionOfDone[]

  @Action(Actions.CONVERT_ITEM_TO_EPIC) covertItemToEpic: ({
    item
  }: {
    item: Item
  }) => Promise<Epic>
  @Action(Actions.POST_ITEM) postItem: ({
    item,
    onlySend
  }: {
    item: Item
    onlySend?: boolean
  }) => Promise<Item | false>
  @Action(Actions.UPDATE_TASKS_WITH_CLONE) setTasks: (tasks: Task[]) => void
  @Action(Actions.CHANGE_OWNER_ITEM) updateOwner: (changeOwner: ChangeItemOwner) => void
  @Action(Actions.SET_CUSTOM_SESSION_PROJECT) setCustomSessionProject: (
    project: Project | null
  ) => void
  @Action(Actions.RESOLVE_SESSION_PROJECT_ID) getProjectByUrl: ({
    name,
    onlyGet
  }: {
    name: string
    onlyGet?: boolean
  }) => Promise<string>
  @Action(Actions.CHANGE_STATUS_ITEM) updateStatus: ({
    changeStatus
  }: {
    changeStatus: ChangeItemStatus
  }) => void
  @Action(Actions.RESOLVE_ITEM_ID) resolveItemId: ({
    sequenceNumber,
    onlyGet,
    projectId
  }: {
    sequenceNumber: number
    onlyGet?: boolean
    projectId?: string
  }) => Promise<string>
  @Action(Actions.SET_SESSION_ITEM_ID) setSessionItemId: (id: string) => void
  @Action(Actions.PUT_TASKS_WITH_CHANGES_CHECKING) saveTasks: (payload: UpdateTaskList) => void
  @Action(Actions.RESET_CLONE) resetClone: () => void
  @Action(Actions.CHANGE_SPRINT_PRIORITY) changeSprintPriority: (payload: ItemQueryPriority) => void
  @Action(Actions.IS_WIP_LIMIT_VALID) checkWipLimitValid: ({
    sprintId,
    itemType
  }: {
    sprintId: string
    itemType: ItemType
  }) => Promise<boolean>
  @Action(Actions.RESET_ITEM) resetItem: () => void
  @Action(Actions.RESET_TASKS) resetTasksList: () => void
  @Action(Actions.GET_ITEM_ANALYTICS) getItemAnalytics: ({
    itemId,
    projectId
  }: {
    itemId: string
    projectId: string
  }) => void
  @Action(Actions.GET_TIPS_STEPS) getTipsSteps: ({
    learnStep,
    customTipsSteps
  }: {
    learnStep: TipsKeyType
    customTipsSteps?: TipsStepType[]
  }) => void
  @Action(Actions.PUT_ITEM) putItemAndClone: (item: Item) => void
  @Action(Actions.RESET_TIPS_DATA) resetTipsData: () => void
  @Action(Actions.RESET_ALL_DEFINITIONS) resetAllDefinitions: () => void
  @Action(Actions.UPDATE_DEFINITION_OF_DONE_LIST) updateDefinitionOfDoneList: (
    definition: DefinitionOfDone[]
  ) => void
  @Action(Actions.APPLY_TASKS_FROM_SSE) applyTasksFromSSE: (payload: {
    oldValue: Task[]
    newValue: Task[]
  }) => void
  @Action(Actions.RESTORE_TASK_DRAFT) restoreTaskDraft: (payload: {
    localTasks: Task[]
    edits: TaskDraftEdit[]
    serverTasks: Task[]
  }) => Promise<Task[]>

  gsapAnimationLeft: gsap.core.Timeline | null = null
  gsapAnimationRight: gsap.core.Timeline | null = null
  htmlElementInList: HTMLElement | null = null
  isLazySave = false
  showModalEstimationRequired = false
  showNavigationsBtn = false
  nextItem: null | Item = null
  prevItem: null | Item = null
  showLeftNavigationBtn = false
  showRightNavigationBtn = false
  showBottomNavigationBtn = false
  navigationSideBtnPositionTop = 0
  navigationSideBtnPositionBottom = 0
  showSkeletonByNavigation = false
  goToAnotherItem: boolean | null = null
  unsavedChanges = false
  continuedUnsaveChanges = false
  /** Keeps the restored draft marked as edited only when it was edited before reload. */
  itemRestoredAfterReload = false
  restoredDraftHasBaseline = false
  itemId = ""
  showAnalytics = false
  analyticsAnimationDuration = 0.3
  itemLeftStep: TipsStepType = "itemLeft"
  itemRightStep: TipsStepType = "itemRight"
  eventQueue: ServerSendEventNotification[] = []
  forceUpdate = 0
  labelForceUpdate = 0
  saveRequestQueue = new RequestQueue()
  readonly myWorkSectionIds: MyWorkSectionId[] = ["activeWork", "workedOn", "completed"]

  get projectId() {
    return this.item.projectId ? this.item.projectId : this.sessionProjectId
  }

  get subscriptionItem() {
    return this.serverSendUpdated("ITEM_UPDATED")
  }

  get subscriptionItemPriorityChanged() {
    return this.serverSendUpdated("ITEM_PRIORITY_CHANGED")
  }

  get sprintChangedEvent() {
    return this.serverSendUpdated("SPRINT_CHANGED")
  }

  get inActiveSprint() {
    return this.item?.sprint?.isStarted
  }

  get itemIsLoaded() {
    return (
      !this.$wait.is("get.item") &&
      !this.$wait.is("loadingItemLeft") &&
      !this.$wait.is("loadingItemRight") &&
      !this.$wait.is("checkUserInItem")
    )
  }

  get itemIsEdited() {
    if (!this.itemIsLoaded || !this.$route.query.i) return false
    if (this.itemRestoredAfterReload) return true
    if (this.backlogCreatedItem && this.backlogCreatedItem?.isInline) return true
    return this.itemId ? this.item.isModified(this.item.unsaveFields) : this.item.isModified()
  }

  get loadingAttachment() {
    return this.$wait.is("get.attachment")
  }

  created() {
    emitterOn("beforeInactiveReload", this.storeItemBeforeReload)
    this.onShowItem()
  }

  async onOpenItem() {
    // return
    if (!this.$route.query.i) return
    this.itemRestoredAfterReload = false
    this.restoredDraftHasBaseline = false
    this.$wait.start("get.item")
    this.onResetItem()
    this.resetBtn()
    const itemQuery = this.$route.query.i as string
    let projectId = ""
    const lastDashIndex = itemQuery.lastIndexOf("-")
    let sequenceNumber = 0
    if (lastDashIndex !== -1) {
      sequenceNumber = Number(itemQuery.slice(lastDashIndex + 1))
      projectId = await this.getProjectByUrl({
        name: itemQuery.slice(0, lastDashIndex),
        onlyGet: this.item.project?.urlName === this.$route.params.projectId
      })
    } else {
      sequenceNumber = Number(this.$route.query.i)
    }
    this.itemId = sequenceNumber
      ? await this.resolveItemId({
          sequenceNumber,
          onlyGet: true,
          projectId
        })
      : ""
    if (this.$route.query.i !== itemQuery) return
    /** Keep the AI conversation context aligned with the Item opened inside a backlog. */
    this.setSessionItemId(this.itemId)
    if (this.itemId) {
      await this.get({ itemId: this.itemId, projectId })
      if (!this.$route.query.i) {
        this.onResetItem()
        this.onForceUpdateEditItem()
        return
      }
      window.addEventListener("resize", this.setNavigationBtnPosition)
    } else {
      const draftItem =
        this.backlogCreatedItem && !this.backlogCreatedItem.id
          ? new Item(this.backlogCreatedItem)
          : new Item()

      draftItem.epic = this.epic
      this.setItem(draftItem)

      if (!this.backlogCreatedItem || this.backlogCreatedItem.id) {
        this.openedItemBacklogSetupChange(null)
      }

      if (this.backlogCreatedItem?.id) {
        this.setCreatedBacklogItem(null)
      }
    }
    const restoredUnsavedChanges = await this.restoreItemAfterReload()
    this.itemRestoredAfterReload = restoredUnsavedChanges
    this.setAnimationData()
    if (!this.restoredDraftHasBaseline) this.setInitialState()
    const newBacklogInstanceName = Item.getBacklogInstanceNameByItem(this.item)
    this.updateBacklogInstanceName(newBacklogInstanceName)
    this.$wait.end("get.item")
    if (restoredUnsavedChanges) {
      this.$nextTick(() => {
        this.unsavedChanges = true
        this.$emit("unsavedChanges", true)
      })
    }
    await this.loadCurrentItemIndex()
    this.setNavigation()
    if (this.item.project) {
      this.setCustomSessionProject(new Project(this.item.project))
    }
    this.$nextTick(() => {
      window.addEventListener("resize", this.onResize)
      this.onGetSteps()
    })
  }

  /** Captures only local Item and task edits so a reload can merge drafts with fresh data. */
  storeItemBeforeReload() {
    if (!this.$route.query.i) return
    this.$emit("unsavedChanges", false)
    try {
      const sessionData: ItemSessionData = {
        item: this.item,
        tasks: this.tasks,
        unsavedChanges: this.unsavedChanges,
        itemFieldsChanged: this.item.unsaveFields.filter(
          field => !isEqual(this.item[field], this.itemClone?.[field])
        ),
        taskDraftEdits: this.taskDraftEdits
      }
      sessionStorage.setItem(ITEM_SESSION_KEY, JSON.stringify(sessionData))
    } catch {
      return
    }
  }

  /** Applies unsaved fields over the freshly loaded Item instead of restoring an outdated full snapshot. */
  async restoreItemAfterReload() {
    try {
      const storedItem = sessionStorage.getItem(ITEM_SESSION_KEY)
      if (!storedItem) return false

      const parsedData = JSON.parse(storedItem) as ItemSessionData | Item
      const sessionData =
        "item" in parsedData ? parsedData : { item: parsedData, tasks: [], unsavedChanges: true }
      if (sessionData.unsavedChanges === false) {
        sessionStorage.removeItem(ITEM_SESSION_KEY)
        return false
      }
      if (sessionData.item.id && this.item?.id && sessionData.item.id !== this.item.id) {
        sessionStorage.removeItem(ITEM_SESSION_KEY)
        return false
      }
      if (this.item?.id && sessionData.itemFieldsChanged && sessionData.taskDraftEdits) {
        const savedItem = Item.create(sessionData.item)
        const freshItem = Item.create(this.item)
        freshItem.setInitialState()
        sessionData.itemFieldsChanged.forEach(field => {
          Object.assign(freshItem, { [field]: savedItem[field] })
        })
        freshItem.tasks = await this.restoreTaskDraft({
          localTasks: sessionData.tasks,
          edits: sessionData.taskDraftEdits,
          serverTasks: freshItem.tasks
        })
        this.setItem(freshItem)
        this.restoredDraftHasBaseline = true
        sessionStorage.removeItem(ITEM_SESSION_KEY)
        return true
      }
      const restoredItem = Item.create(sessionData.item)
      const restoredTasks = sessionData.tasks.map(task => Task.create(task))
      restoredItem.tasks = restoredTasks
      this.setItem(restoredItem)
      this.setTasks(restoredTasks)
      sessionStorage.removeItem(ITEM_SESSION_KEY)
      return sessionData.unsavedChanges ?? true
    } catch {
      return false
    }
  }

  onResize() {
    this.calculateTipsPosition()
    this.setNavigationBtnPosition()
  }

  onGetSteps() {
    this.getTipsSteps({ learnStep: "showFirstItemCreationTips" })
    if (this.selectedStep) {
      const activeElement = document.activeElement as HTMLElement
      if (activeElement) {
        activeElement.blur()
      }
    }
    this.calculateTipsPosition()
  }

  calculateTipsPosition() {
    const tipsLeftElement = document.getElementById("editItemLeftTips") as HTMLElement
    const itemLeftElement = this.$refs.itemLeft as HTMLElement
    if (tipsLeftElement && itemLeftElement) {
      positionTipsByElement(itemLeftElement, tipsLeftElement)
    }
    const tipsRightElement = document.getElementById("editItemRightTips") as HTMLElement
    const itemRightElement = this.$refs.itemRight as HTMLElement
    if (tipsRightElement && itemRightElement) {
      positionTipsByElement(itemRightElement, tipsRightElement)
    }
  }

  @Emit()
  updateBacklogInstanceName(instanceName) {
    return instanceName
  }

  @Emit()
  openedItemBacklogSetupChange(backlogSetup: BacklogSetup | null) {
    return backlogSetup
  }

  setNavigation() {
    this.showNavigationsBtn = true
    this.setNavigationBtnPosition()

    this.setBtnData()
  }

  getCurrentBacklogItems() {
    let data = store.getters[Getters[this.backlogSetup.storeGetter]] as ServerResponse<Item>

    if (this.backlogSetup?.sprintInfo?.id) {
      data = data?.[this.backlogSetup.sprintInfo.id]
    }

    return data?.content ?? []
  }

  setNavigationBtnPosition() {
    const container = document.querySelector("#edit-item__left")
    if (container) {
      const rect = container.getBoundingClientRect()
      this.navigationSideBtnPositionTop = rect.top
      this.navigationSideBtnPositionBottom = rect.bottom
    }
  }

  async setBtnData() {
    let data = store.getters[Getters[this.backlogSetup.storeGetter]] as ServerResponse<Item>

    if (this.backlogSetup?.sprintInfo?.id) {
      data = data?.[this.backlogSetup.sprintInfo.id]
    }
    if (!data) return
    const itemsList = this.getCurrentBacklogItems()
    const currentItemPosition = itemsList.findIndex(item => item.id === this.item.id)
    const lastElementIndex = itemsList.length - 1
    let isLast = data?.last ?? true
    if (currentItemPosition === lastElementIndex && !isLast) {
      const successfulRequest = await this.updateBacklog({
        page: this.backlogSetup.filter.page + 1
      })
      if (!successfulRequest) {
        isLast = true
        return
      }
      return this.setBtnData()
    }
    this.prevItem = itemsList[currentItemPosition - 1] ?? null
    this.nextItem = itemsList[currentItemPosition + 1] ?? null
  }

  async updateBacklog(payload = { page: 1 }) {
    this.backlogSetup.filter.page = payload.page ?? 1
    return await backlogModule[BacklogActions[this.backlogSetup.backlogGetAction]](
      this.backlogSetup
    )
  }

  async handleInput(params: UpdateObjectValue<Item>) {
    await this.syncItemField(params)
  }

  handleInputLocal(params: UpdateObjectValue<Item>) {
    this.updateItemValue(params)
  }

  async handleInputAndSave(params: UpdateObjectValue<Item>) {
    if (this.item?.id) {
      this.updateItemCloneValue(params)
      await this.enqueueSaveRequest(async () => {
        await this.updateItem({ item: this.itemClone, notSetItem: true })
      })
    }
    this.updateItemValue(params)
  }

  /** Persists completed Acceptance Criteria and keeps the item dirty state unchanged. */
  async completeAcceptanceCriteria(params: UpdateObjectValue<Item>) {
    if (this.item?.id) {
      this.updateItemCloneValue(params)
    }
    this.updateItemValue(params)
    this.item.markFieldAsSaved("acceptanceCriteria")
    if (!this.item?.id) return

    await this.enqueueSaveRequest(async () => {
      await this.updateItem({ item: this.itemClone, notSetItem: true })
    })
  }

  async checkRemoveItemFromBacklog() {
    if (!this.item.id || this.hasDeferredDeleteForCurrentItem()) return

    if (this.backlogType === "backlogMyWork") {
      await this.updateMyWorkItemInBacklog()
      return
    }
    const itemInBacklog = await this.checkItemInBacklog()
    if (itemInBacklog) {
      this.onSetBacklogItem(itemInBacklog)
      return
    }

    await this.onRemoveItemWithAnimation({
      itemId: this.item.id,
      nameBacklog: this.backlogType,
      sprintId: this.backlogSetup.sprintInfo?.id
    })
    const backlogName = Item.getBacklogTypeByItem(this.item)
    if (
      backlogName === "backlogUnprioritized" ||
      backlogName === this.backlogType ||
      this.itemMoveToParams
    ) {
      return
    }

    this.insertItemInBacklog({
      backlogName: Item.getBacklogTypeByItem(this.item),
      item: this.item,
      sprintId: this.item.sprint?.id ?? "",
      index: null
    })
  }

  async updateMyWorkItemInBacklog() {
    const itemId = this.item.id
    if (!itemId) return

    const previousSectionId = this.getMyWorkItemSectionId(
      store.getters[Getters.GET_MY_WORK_SECTIONS],
      itemId
    )
    const updatedMyWork = await this.getUpdatedMyWork()
    if (!updatedMyWork) {
      this.getHtmlElementInList()
      return
    }
    const nextSectionId = this.getMyWorkItemSectionId(updatedMyWork, itemId)
    const sectionChanged = previousSectionId !== nextSectionId
    const element = document.getElementById(itemId)

    if (sectionChanged && element) {
      await new Promise<void>(resolve => {
        reverseScaleAnimation(element)
          .eventCallback("onComplete", () => resolve())
          .play()
      })
    }

    backlogModule[BacklogActions.SET_MY_WORK](updatedMyWork)

    if (!sectionChanged) {
      this.getHtmlElementInList()
      return
    }

    this.htmlElementInList = null
    if (!nextSectionId) return

    this.$nextTick(() => {
      const newElement = document.getElementById(itemId)
      if (newElement) {
        forwardScaleAnimation(newElement).play()
      }
    })
  }

  async getUpdatedMyWork() {
    const backlogSetup = cloneDeep(this.backlogSetup)
    backlogSetup.filter.onlyGet = true
    return await backlogModule[BacklogActions.GET_MY_WORK](backlogSetup)
  }

  getMyWorkItemSectionId(
    myWork: MyWork | null | undefined,
    itemId: string
  ): MyWorkSectionId | null {
    return (
      this.myWorkSectionIds.find(sectionId =>
        myWork?.[sectionId]?.some(item => item.id === itemId)
      ) ?? null
    )
  }

  async checkItemInBacklog() {
    if (!this.item.id) return
    const backlogSetup = cloneDeep(this.backlogSetup)
    backlogSetup.filter.page = 1
    backlogSetup.filter.onlyGet = true
    backlogSetup.filter.itemIds = [this.item.id]

    const itemInBacklogResponse =
      await backlogModule[BacklogActions[backlogSetup.backlogGetAction]](backlogSetup)
    const itemInBacklog = itemInBacklogResponse.content
    return itemInBacklog?.[0]
  }

  statusUpdate(params: UpdateStatusParams) {
    this.updateStatus({
      changeStatus: {
        ...params,
        projectId: this.projectId,
        itemId: this.item.id as string,
        oldStatus: this.item.status,
        itemTitle: this.item.title
      }
    })
  }

  async onHideItem() {
    if (!this.itemIsLoaded) {
      this.onScale()
      this.onResetAll()
      return
    }
    await this.checkModified()
    this.openedItemBacklogSetupChange(null)
    // TODO: need to refactor closing item so that methods for opening work only after the previous element is closed
    if (this.$route.query.i) return
    this.onScale()
    this.onResetAll()
  }

  async checkModified() {
    if (this.continuedUnsaveChanges) {
      this.continuedUnsaveChanges = false
      return
    }

    if (
      this.item.isModified() ||
      this.taskIsEdited ||
      this.itemMoveToParams ||
      this.item.isDeleted ||
      this.backlogCreatedItem?.id
    ) {
      if (this.teamId) {
        this.backlogSetup.filter.setFilterTeams([this.teamId])
      }
      await this.moveItemToAnotherBacklog(this.item)
      await Promise.all([
        this.getMetrics(this.backlogSetup),
        this.setInitialState(),
        this.checkRemoveItemFromBacklog(),
        this.updateMovedItem()
      ])
    }
  }

  async updateMovedItem() {
    if (!this.itemMoveToParams) return
    await this.get({ itemId: this.itemId, projectId: this.projectId })
    this.insertItemInBacklog({ ...this.itemMoveToParams, item: this.item })
    this.itemMoveToParams = null
  }

  onResetItem() {
    this.resetItem()
    this.resetTasksList()
    this.resetBtn()
    this.setInitialState()
    this.resetAnalyticsAnimation()
  }

  onResetAll() {
    this.onResetItem()
    this.setCreatedBacklogItem(null)
    this.onRemoveItemQuery()
    this.resetInlineItem()
    this.onForceUpdateEditItem()
    this.setCustomSessionProject(null)
    if (this.item.project?.urlName !== this.$route.params.projectId) {
      this.getProjectByUrl({
        name: this.$route.params.projectId as string
      })
    }
  }

  onDiscardCreatedItem() {
    this.showModalEstimationRequired = false
    this.onRemoveItemQuery()
  }

  @Emit()
  resetInlineItem() {
    //
  }

  async onRemoveItemWithAnimation(params: {
    itemId: string
    nameBacklog: string
    sprintId?: string
  }) {
    if (!this.htmlElementInList) {
      return
    }

    if (this.hasDeferredDeleteForCurrentItem()) {
      this.htmlElementInList = null
      return
    }

    await new Promise(resolve => {
      reverseScaleAnimation(this.htmlElementInList as any)
        .play()
        .eventCallback("onComplete", () => {
          resolve(this.onRemoveItem(params))
          this.htmlElementInList = null
        })
    })
  }

  onRemoveItem(params) {
    this.removeItemFromBacklog(params)
    this.getAllMetrics()
  }

  hasDeferredDeleteForCurrentItem() {
    const currentItemId = this.item?.id

    return (
      !!currentItemId &&
      this.deferredDeletedEvents.some(deferredEvent => deferredEvent.itemId === currentItemId)
    )
  }

  onSetBacklogItem(item) {
    this.insertItemInBacklog({
      backlogName: this.backlogType,
      item,
      sprintId: this.backlogSetup?.sprintInfo?.id,
      index: null
    })
  }

  @Watch("loadingAttachment")
  onLazySave() {
    if (this.isLazySave && !this.loadingAttachment) {
      this.isLazySave = false
      this.save()
    }
  }

  enqueueSaveRequest(request: () => Promise<unknown>): Promise<unknown> {
    return this.saveRequestQueue.enqueue({
      key: "saveRequestItem",
      promise: request
    })
  }

  async syncItemField(params: UpdateObjectValue<Item>, enqueueRequest = true) {
    if (!this.item?.id) {
      await this.updateItemValue(params)
      return
    }

    if (params["id"] === "itemTypeParsed") {
      this.onUpdatedItemType(params)
    }

    if (this.item.getAutoSaveFields(params["id"])) {
      this.updateItemCloneValue(params)

      if (enqueueRequest) {
        await this.enqueueSaveRequest(async () => {
          await this.updateItem({ item: this.itemClone, notSetItem: true })
        })
      } else {
        await this.updateItem({ item: this.itemClone, notSetItem: true })
      }
    }

    if (params["id"] === "owner") {
      await this.updateOwner({
        itemId: this.item?.id ?? "",
        ownerId: params["value"]?.id,
        projectId: this.projectId
      })
      this.updateItemCloneValue(params)
    }

    this.updateItemValue(params)
  }

  onUpdatedItemType(params: UpdateObjectValue<Item>) {
    const backlogSetupId = Item.getBacklogInstanceNameByItem(this.item)
    const backlogConfig = backlogSetupId.includes("BacklogSprint")
      ? BacklogSetup.BacklogSprint
      : BacklogSetup[backlogSetupId]
    const shouldResetPriority =
      !backlogConfig.acceptType.includes(params["value"].id) ||
      backlogSetupId === "BacklogUnprioritized"

    if (!shouldResetPriority) return

    this.currentItemIndexRequestId++
    this.currentItemIndex = null
    ;[
      { id: "priority", value: null },
      { id: "backlogPriority", value: null },
      { id: "unprioritizedOrder", value: undefined }
    ].forEach(payload => {
      this.updateItemCloneValue(payload as unknown as UpdateObjectValue<Item>)
      this.updateItemValue(payload as unknown as UpdateObjectValue<Item>)
    })
  }

  async saveItem() {
    await this.onUpdateTasks()
    await this.save()
  }

  async save() {
    if (this.$wait.is("itemSave") || this.isLazySave || !this.itemIsEdited) return
    if (this.loadingAttachment) {
      this.isLazySave = true
      return
    }
    this.$wait.start("itemSave")
    try {
      const isSaved = this.itemId ? await this.onUpdateItem() : await this.onCreateItem()
      if (!isSaved) {
        return false
      }

      this.itemRestoredAfterReload = false
      await this.resetClone()
      this.$emit("saveItem")
      if (!this.htmlElementInList) {
        this.getHtmlElementInList()
      }
    } finally {
      this.$wait.end("itemSave")
    }
  }

  async onUpdateItem() {
    const updatedItem = await this.updateItem({ item: this.item })
    if (!updatedItem) {
      return false
    }
    await this.checkRemoveItemFromBacklog()
    await this.loadCurrentItemIndex()
    this.setInitialState()
    return true
  }

  async onCreateItem() {
    // Check can add item to the sprint
    if (this.backlogSetup.sprintInfo?.status === "STARTED" && !this.canAddItemIntoActiveSprint()) {
      return false
    }
    if (this.item.sprint?.continuousSprint) {
      const wipLimitValid = await this.checkWipLimitValid({
        sprintId: this.item.sprint.id,
        itemType: this.item.itemTypeParsed.id
      })
      if (!wipLimitValid) {
        this.sprintWipLimitExceeded = this.item.sprint
        return false
      }
    }
    // Handle input for tasks
    this.handleInput({ id: "tasks", value: this.tasks } as any)
    // Handle input for definitions of done
    this.handleInput({
      id: "definitionsOfDone",
      value: this.enabledDefinitions
    } as any)

    // Post the item to the server
    const createdItem = await this.postItem({ item: this.item })
    if (!createdItem) {
      return false
    }
    // Replace the current route query with the item's sequence number
    await this.$router.replace({
      query: {
        ...this.$route.query,
        i: createdItem.sequenceNumber
      }
    })
    // Set the itemId to the created item's id
    this.itemId = createdItem.id ?? ""
    // Set the tasks for the item
    this.setTasks(this.tasks)
    // Check if the item exists in the backlog
    const itemInBacklog = await this.checkItemInBacklog()
    // Insert the item into the appropriate backlog
    const index = this.backlogCreatedItem?.priority
      ? (this.backlogCreatedItem.priority.index ?? null)
      : this.customIndexForNewItem
    this.insertItemInBacklog({
      item: this.item,
      index,
      backlogName: itemInBacklog ? this.backlogType : "backlogUnprioritized",
      sprintId: this.item.sprintId
    })
    // Reset the created backlog item
    this.setCreatedBacklogItem(null)
    // Reset the inline item
    this.resetInlineItem()
    // Set the item's initial state
    this.setInitialState()
    this.getAllMetrics()
    return true
  }

  onRemoveItemQuery() {
    if (!this.$route.query.i) return
    this.$router.go(-1)
  }

  async onUpdateTasks() {
    await this.enqueueSaveRequest(async () => {
      if (!this.item?.id) return

      await this.saveTasks({
        tasks: this.tasks,
        item: this.item,
        projectId: this.projectId
      })
      const taskOwners = {
        value: this.tasks.filter(task => task.owner).map(e => e.owner),
        id: "taskOwners"
      } as any
      await this.syncItemField(taskOwners, false)
    })
  }

  itemLeftBeforeEnter(animateElement) {
    const elementWrapper = document.getElementById("edit-item__left") as HTMLElement
    this.getHtmlElementInList()
    this.gsapAnimationLeft = storyOpeningAnimation({
      animateElement,
      itemElement: this.htmlElementInList,
      elementWrapper
    })
      .eventCallback("onComplete", () => {
        this.$emit("showItem", true)
      })
      .play()
  }

  getHtmlElementInList() {
    this.htmlElementInList = this.item.id ? document.getElementById(this.item.id) : null
  }

  itemLeftBeforeLeave() {
    this.$emit("showItem", false)
    this.showNavigationsBtn = false
    if (!this.gsapAnimationLeft) {
      this.onCompleteAnimation()
      window.removeEventListener("resize", this.setNavigationBtnPosition)
      return
    }
    this.gsapAnimationLeft.reverse(0).eventCallback("onReverseComplete", () => {
      this.onCompleteAnimation()
    })
    this.resetTipsData()
    window.removeEventListener("resize", this.setNavigationBtnPosition)
  }

  onCompleteAnimation() {
    this.onHideItem()
    this.$emit("hideItem")
  }

  onScale() {
    if (!this.htmlElementInList) return
    storyScalingAnimation(this.htmlElementInList).play()
  }

  itemRightBeforeLeave() {
    if (!this.gsapAnimationRight) return
    this.gsapAnimationRight.reverse(0)
  }

  itemRightBeforeEnter(animateElement) {
    const elementWrapper = document.getElementById("edit-item__right") as HTMLElement
    this.gsapAnimationRight = storyOpeningAnimation({
      animateElement,
      itemElement: this.htmlElementInList,
      elementWrapper
    }).play()
  }

  setAnimationData() {
    this.getHtmlElementInList()
    const elementWrapperRight = document.getElementById("edit-item__right") as HTMLElement
    const elementWrapperLeft = document.getElementById("edit-item__left") as HTMLElement
    const elementRight = this.$refs.itemRight as HTMLElement
    const elementLeft = this.$refs.itemLeft as HTMLElement
    if (elementRight && !this.gsapAnimationRight) {
      this.gsapAnimationRight = storyOpeningAnimation({
        animateElement: elementWrapperRight,
        itemElement: this.htmlElementInList,
        elementWrapper: elementRight
      })
    }
    if (elementLeft && !this.gsapAnimationLeft) {
      this.gsapAnimationLeft = storyOpeningAnimation({
        animateElement: elementLeft,
        itemElement: this.htmlElementInList,
        elementWrapper: elementWrapperLeft
      })
    }
  }

  async moveItemToAnotherBacklog(item) {
    if (!this.backlogCreatedItem || this.backlogCreatedItem.id !== item.id || !this.item.id) {
      return
    }

    const sprint = this.backlogCreatedItem?.sprint
    const isActiveSprint = sprint?.status === "STARTED"
    const storyPoint = item.storyPoints !== null
    if (!isActiveSprint) {
      return
    }
    const alertContent: AlertContentItem[] = []
    if (
      (!this.backlogCreatedItem?.sprintIsContinuous && (!this.estimatedTasks || !storyPoint)) ||
      !this.estimatedTasks
    ) {
      const toSprintNumber = this.backlogSetup.sprintInfo?.sequenceNumber
        ? `#${this.backlogSetup.sprintInfo.sequenceNumber}`
        : ""
      alertContent.push(
        {
          text: `Item #${item.sequenceNumber} `,
          type: "bold"
        },
        {
          text: "moved back to the ",
          type: "regular"
        },
        {
          text: `${$t(`backlog.titles['${this.backlogSetup.storeBacklogId}']`) + toSprintNumber}`,
          type: "bold"
        }
      )
    } else {
      const toSprintNumber = sprint?.sequenceNumber ? `#${sprint.sequenceNumber}` : ""
      alertContent.push(
        {
          text: `Item #${item.sequenceNumber} `,
          type: "bold"
        },
        {
          text: "estimated and moved to the ",
          type: "regular"
        },
        {
          text: `${
            $t(`backlog.titles['${this.backlogCreatedItem?.backlogSetup?.storeBacklogId}']`) +
            toSprintNumber
          }`,
          type: "bold"
        }
      )
      const priority = this.backlogCreatedItem.priority
      this.handleInput({
        id: "sprint",
        value: sprint
      } as any)

      this.handleInput({
        id: "priority",
        value: priority
      } as any)
      await this.changeSprintPriority(
        new ItemQueryPriority({
          previousItemId: priority?.previousItemId,
          itemId: item.id ?? "",
          nextItemId: priority?.nextItemId,
          priorityType: priority?.priorityType as ItemQueryPriorityType,
          sprintId: sprint?.id ?? ""
        })
      )
      this.insertItemInBacklog({
        item,
        index: this.backlogCreatedItem.priority?.index ?? 0,
        backlogName: this.backlogCreatedItem?.backlogSetup?.backlogType ?? "",
        sprintId: this.backlogCreatedItem?.sprint?.id ?? ""
      })
    }
    alertModule[AlertActions.SHOW_ALERT]({
      type: AlertType.SUCCESS,
      theme: "toast",
      content: alertContent
    })
    this.setCreatedBacklogItem(null)
  }

  onCloseEstimationModal() {
    this.showModalEstimationRequired = false
  }

  onShowModalEstimationRequired() {
    this.showModalEstimationRequired = true
  }

  resetBtn() {
    this.nextItem = null
    this.prevItem = null
  }

  async goToNextItem() {
    this.onGoToAnotherItem(true)
  }

  async onGoToAnotherItem(next) {
    this.goToAnotherItem = next
    if (this.unsavedChanges) {
      this.$modal.show("unsaved-changes")
      return
    }

    const animationMethod = next ? slideItemAnimationRight : slideItemAnimationLeft
    this.showSkeletonByNavigation = true
    this.showNavigationsBtn = false
    const elementLeft = this.$refs.itemLeft as HTMLElement
    const elementRight = this.$refs.itemRight as HTMLElement
    const skeletonLeft = document.getElementById("skeletonLeft")
    const skeletonRight = document.getElementById("skeletonRight")
    animationMethod(elementLeft, skeletonLeft).play()
    animationMethod(elementRight, skeletonRight)
      .play()
      .eventCallback("onComplete", () => {
        this.navigateAfterHidingItem(next)
      })
  }

  async navigateAfterHidingItem(next) {
    await this.checkModified()
    const selectedItem = next ? this.nextItem : this.prevItem
    if (!selectedItem) return
    await this.$router.replace({
      query: {
        ...this.$route.query,
        i: selectedItem.project
          ? `${selectedItem.project.urlName}-${selectedItem.sequenceNumber}`
          : selectedItem.sequenceNumber
      }
    })
    this.onForceUpdateEditItem()
  }

  async goToPrevItem() {
    this.onGoToAnotherItem(false)
  }

  @Emit()
  onForceUpdateEditItem() {
    return
  }

  @Watch("itemIsLoaded")
  setInitialState() {
    this.item.setInitialState()
  }

  @Watch("taskIsEdited")
  @Watch("itemIsEdited")
  unsavedItem() {
    this.unsavedChanges = this.itemIsLoaded ? this.taskIsEdited || this.itemIsEdited : false
    this.$emit("unsavedChanges", this.unsavedChanges)
  }

  async onCovertItemToEpic() {
    this.unsavedChanges = false
    this.$emit("unsavedChanges", this.unsavedChanges)
    const epic = await this.covertItemToEpic({ item: this.item })
    if (!epic) return
    this.$router.replace({
      name: "epic",
      params: {
        epicId: epic.sequenceNumber
      }
    })
  }

  @Watch("backlogSetup.storeGetter")
  navigationDataUpdate() {
    this.setBtnData()
  }

  @Watch("$route.query.i")
  async onShowItem() {
    if (!this.$route.query.i) {
      this.setSessionItemId("")
      return
    }
    if (this.$wait.is("itemSave") || this.showSkeletonByNavigation) return
    await this.onOpenItem()
  }

  @Watch("continueUnsave")
  onContinueUnsave() {
    this.onResetItem()

    if (this.goToAnotherItem !== null) {
      this.item.setInitialState()
      this.continuedUnsaveChanges = true
      this.$nextTick(() => {
        this.onGoToAnotherItem(this.goToAnotherItem)
      })
    }
  }

  createEditorData(params: UpdateObjectValue<Item>) {
    this.updateItemValue(params)
    this.setInitialState()
  }

  async moveToUnprioritized() {
    this.handleInput({
      id: "sprintId",
      value: null
    } as any)
    this.handleInput({
      id: "priority",
      value: null
    } as any)

    if (this.item.itemType === "SUPPORT_CASE") {
      this.updateItemValue({
        id: "team",
        value: null
      } as any)
    }

    const createdItem = await this.postItem({ item: this.item })
    if (!createdItem) {
      return false
    }
    this.setInitialState()
    this.closeEstimationModal()
    const query = { ...this.$route.query }
    delete query.i
    this.$router.replace({ query })
    this.$emit("updateUnprioritizedBacklog")
    return true
  }

  closeEstimationModal() {
    this.showModalEstimationRequired = false
  }

  toggleShowAnalytics() {
    if (!this.item.id) return
    if (this.showAnalytics) {
      this.onHideAnalytics()
    } else {
      this.onShowAnalytics()
    }
  }

  onShowAnalytics() {
    if (!this.item.id) return
    const params: { itemId: string; projectId: string; teamType?: string } = {
      itemId: this.item.id,
      projectId: this.projectId
    }
    this.getItemAnalytics(params)
    slideAnimation("#itemRightContent", this.analyticsAnimationDuration)
      .play()
      .eventCallback("onComplete", () => {
        this.showAnalytics = true
        slideAnimation("#itemAnalytics", this.analyticsAnimationDuration)
          .reverse(0)
          .eventCallback("onReverseComplete", () => {
            removeTransform("#itemAnalytics").play()
          })
      })
  }

  onHideAnalytics() {
    slideAnimation("#itemAnalytics", this.analyticsAnimationDuration)
      .play()
      .eventCallback("onComplete", () => {
        this.showAnalytics = false
        slideAnimation("#itemRightContent", this.analyticsAnimationDuration).reverse(0)
        removeTransform("#itemRightContent").play()
      })
  }

  resetAnalyticsAnimation() {
    if (!this.showAnalytics) return
    this.showAnalytics = false
    slideAnimation("#itemAnalytics", 0).play()
    slideAnimation("#itemRightContent", 0).reverse(0)
    removeTransform("#itemRightContent").play()
  }

  async onSaveTasks() {
    await this.enqueueSaveRequest(async () => {
      if (!this.item?.id) return

      await this.saveTasks({
        tasks: this.tasks,
        item: this.item
      })
    })
  }

  /** Keeps an open backlog item's sprint navigation current when the sprint ends. */
  @Watch("sprintChangedEvent")
  onSprintChanged(event: ServerSendSprintUpdateNotification) {
    const sprint = this.item.sprint
    if (
      !this.$route.query.i ||
      !sprint?.id ||
      event?.sprint?.id !== sprint.id ||
      event.sprint.status === sprint.status
    ) {
      return
    }

    this.updateOpenedItemValues({
      payload: [
        { id: "sprint", value: Sprint.create({ ...sprint, status: event.sprint.status }) }
      ] as unknown as UpdateObjectValue<Item>[],
      updateClone: true
    })
  }

  @Watch("subscriptionItem")
  @Watch("subscriptionItemPriorityChanged")
  onSubscriptionItemChange(newEvent: ServerSendEventNotification) {
    if (newEvent.item?.id === this.item.id) {
      this.eventQueue.push(newEvent)
      this.processEventQueue()
    }
  }

  async processEventQueue() {
    if (this.$wait.is("eventProcessing")) {
      return
    }
    this.$wait.start("eventProcessing")
    while (this.eventQueue.length > 0) {
      const event = this.eventQueue.shift()
      if (event) {
        await this.handleEventQueue(event)
      }
    }

    this.$wait.end("eventProcessing")
  }

  async handleEventQueue(event: ServerSendEventNotification) {
    if (event.feContext?.changeId === this.item.feContext.changeId) return
    const eventKeys = Object.keys(event)
    const newItem = { ...event.item }

    const updatedFields = new Set<string>()

    eventKeys.forEach(field => {
      const change = event[field]

      if (field === "definitionOfDoneChanged") {
        const newDef = change.newValue.map(def => DefinitionOfDone.create(def))
        this.updateDefinitionOfDoneList(newDef)
        this.resetAllDefinitions()
        return
      }

      if (field === "taskChanged") {
        this.applyTasksFromSSE({ oldValue: change.oldValue, newValue: change.newValue })
        return
      }

      if (field.includes("Changed")) {
        const itemField = field.replace("Changed", "")

        if (itemField === "title" && this.item.title !== change.oldValue) {
          return
        }

        /** This SSE field contains the comment itself instead of a standard `newValue`. */
        if (itemField === "statusChangeComment") {
          newItem.statusChangeComment = change ? Comment.create(change) : null
          updatedFields.add("statusChangeComment")

          if (change?.structuredDescription) {
            newItem["statusChangeDescription"] = new StructuredDescription({
              html: change.structuredDescription.html,
              text: change.structuredDescription.html,
              json: ""
            })
            updatedFields.add("statusChangeDescription")
          }
          return
        }

        if (itemField === "sprint") {
          const sprint = change.newValue ? event.item?.sprint : null

          newItem.sprintId = change.newValue ?? ""
          newItem.sprint = sprint ?? undefined
          newItem.sprintSequenceNumber = sprint?.sequenceNumber ?? null
          newItem.backlogPriority = event.item?.backlogPriority ?? null
          newItem.unprioritizedOrder = event.item?.unprioritizedOrder ?? undefined
          newItem.priority = event.item?.priority ?? null
          updatedFields.add("sprint")
          return
        }

        if (itemField === "itemType") {
          const backlogSetupId = Item.getBacklogInstanceNameByItem(this.item)
          const backlogConfig = backlogSetupId.includes("BacklogSprint")
            ? BacklogSetup.BacklogSprint
            : BacklogSetup[backlogSetupId]
          const shouldResetPriority =
            !backlogSetupId.includes("BacklogSprint") &&
            (!backlogConfig?.acceptType.includes(change.newValue) ||
              backlogSetupId === "BacklogUnprioritized")

          if (shouldResetPriority) {
            newItem.backlogPriority = null
            newItem.unprioritizedOrder = event.item?.unprioritizedOrder ?? undefined
            newItem.priority = null
          }
        }

        newItem[itemField] = change.newValue
        updatedFields.add(itemField)
      }
    })

    const fieldToRestore: UpdateObjectValue<Item>[] = []
    if (this.itemIsEdited && this.item.title !== this.itemClone.title) {
      newItem.title = this.itemClone.title
      fieldToRestore.push({
        id: "title",
        value: this.item.title
      } as unknown as UpdateObjectValue<Item>)
    }
    if (
      this.itemIsEdited &&
      this.item.structuredDescription?.html !== this.itemClone.structuredDescription?.html
    ) {
      newItem.structuredDescription = this.itemClone.structuredDescription
      fieldToRestore.push({
        id: "structuredDescription",
        value: new StructuredDescription(this.item.structuredDescription)
      } as unknown as UpdateObjectValue<Item>)
    }
    if (
      this.itemIsEdited &&
      this.item.acceptanceCriteria?.html !== this.itemClone.acceptanceCriteria?.html
    ) {
      newItem.acceptanceCriteria = this.itemClone.acceptanceCriteria
      fieldToRestore.push({
        id: "acceptanceCriteria",
        value: this.item.acceptanceCriteria
          ? new StructuredDescription(this.item.acceptanceCriteria)
          : undefined
      } as unknown as UpdateObjectValue<Item>)
    }
    if (this.itemIsEdited && this.item.spec?.html !== this.itemClone.spec?.html) {
      if (!updatedFields.has("spec")) {
        newItem.spec = this.itemClone.spec
      }
      fieldToRestore.push({
        id: "spec",
        value: this.item.spec ? new StructuredDescription(this.item.spec) : undefined
      } as unknown as UpdateObjectValue<Item>)
    }

    this.putItemAndClone(new Item({ ...this.item, ...newItem }))
    this.item.setInitialState()

    if (fieldToRestore.length) {
      fieldToRestore.forEach(params => this.handleInput(params))
    }

    if (updatedFields.has("structuredDescription")) {
      this.forceUpdate++
    }
    if (updatedFields.has("label")) {
      this.labelForceUpdate++
    }

    await this.loadCurrentItemIndexBySSE(event)
  }

  beforeUnmount() {
    emitterDeleteByKey("beforeInactiveReload")
    this.resetItem()
    this.setCustomSessionProject(null)
    this.openedItemBacklogSetupChange(null)
  }
}
